import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Box, Button, Flex, Image, Paragraph, Spinner, Text, useColorMode } from "theme-ui";
import { ActionCard } from "./ActionCard";
import { Inputform } from "../../forms-component/InputForm";
import warningIcon from "../../../Components/assets/images/Icon/Vector.svg";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from 'yup';
import { createActionHighlight, getAllDeliverChecklist } from "../../../store/actions/action.Highlighters";
import toast from "react-hot-toast";

interface FormValues {
    emailLink: string;
    deliveryDate: string;
}

const ActionDeliverNotes = ({ selectedHighlight, showModal, setShowModal, actionObject, keys, stageName }: any) => {
    const dispatch = useDispatch();
    const { stageId } = useParams<{ stageId: string }>();
    const { deliverChecklist, loader, actionHighlights } = useSelector((state: any) => state.actionHighlight);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingFormValues, setPendingFormValues] = useState<FormValues | null>(null);

    const togglePopover = () => {
        setShowModal(false);
        formik.resetForm();
    };

    useEffect(() => {
        if (stageId && showModal) {
            dispatch(getAllDeliverChecklist(stageId));
        }
    }, [dispatch, stageId, showModal]);

    const emailRegex =
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const urlRegex =
        /^(https?:\/\/)([\w.-]+)(:\d+)?(\/[\w\-./?%&=]*)?$/;

    const formik = useFormik<FormValues>({
        enableReinitialize: true,
        initialValues: {
            emailLink: "",
            deliveryDate: "",
        },
        validationSchema: Yup.object({
            emailLink: Yup.string()
                .required("Email or URL is required")
                .test(
                    "is-email-or-url",
                    "Must be a valid email or a URL starting with http:// or https://",
                    (value) => {
                        if (!value) return false;
                        return emailRegex.test(value) || urlRegex.test(value);
                    }
                ),
            deliveryDate: Yup.string().required("Delivery Date is required"),
        }),
        onSubmit: (values) => {
            setPendingFormValues(values);
            setShowConfirmation(true);
        },
    });

    const { handleSubmit, setFieldValue, values, handleBlur, touched, errors } = formik;

    const handleFinalConfirm = () => {
        if (!pendingFormValues) return;

        // Flatten Description and inject taskId into each Description item
        const descriptionsWithTaskId = deliverChecklist.flatMap((item: any) => {

            return (item.payload.Description || []).map((desc: any) => ({
                ...desc,
                taskId: item.taskId
            }));
        });

        const payload = {
            actionType: selectedHighlight.name,
            payload: {
                Description: descriptionsWithTaskId,
                emailLink: pendingFormValues.emailLink,
                deliveryDate: pendingFormValues.deliveryDate,
            },
            ...actionObject,
        };


        dispatch(createActionHighlight(payload, (res: any) => {
            if (res.status === 200) {
                toast.success(`Delivered successfully!`);
            }
        }));

        // Reset everything
        setShowConfirmation(false);
        setShowModal(false);
        formik.resetForm();
        setPendingFormValues(null);
    };
    const [colorMode] = useColorMode();
    const modalHeader = colorMode === 'light' ? "#fff" : "#20283c";
    const label = colorMode === 'light' ? "#0A0A0A" : "#dadada";
    const titleHeader = colorMode === 'light' ? "black" : "#dadada";

    const closeButton = (
        <button
            type="button"
            aria-label="Close"
            onClick={togglePopover}
            style={{
                color: colorMode === 'dark' ? '#fff' : '#000',
                background: 'transparent',
                border: 'none',
                fontSize: 23,
                lineHeight: 1,
                padding: 0,
                cursor: 'pointer'
            }}
        >
            <span aria-hidden="true">&times;</span>
        </button>
    );
    return (
        <>
            <Modal isOpen={showModal} scrollable toggle={togglePopover} size="xl" centered >
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 3.5rem)', overflow: 'hidden' }}>
                    <ModalHeader toggle={togglePopover} close={closeButton} style={{ backgroundColor: modalHeader }}> <Flex sx={{ gap: "0.5rem" }}><img width={22} src={selectedHighlight?.icon} alt="icon" /><Text sx={{ color: '#51E2B5' }}>{'DELIVER'}</Text></Flex></ModalHeader>
                    <ModalBody className="scrollable-modal-body" style={{ backgroundColor: modalHeader, maxHeight: "calc(80vh - 120px)", overflowY: "auto" }}>
                        <Box sx={{ display: "grid", paddingX: "2rem", gap: "1rem", position: "relative" }}>
                            <Paragraph sx={{ fontSize: "18px", fontWeight: "600", mb: "0.5rem", color: label }}>
                                All the reply and conversations
                            </Paragraph>

                            {deliverChecklist?.map((deliver: any, index: number) =>
                                deliver.payload?.Description?.map((action: any, i: number) => (
                                    <ActionCard
                                        key={`${index}-${i}`}
                                        index={i}
                                        action={action}
                                        selectedHighlight={selectedHighlight}
                                        comment={false}
                                        keys={keys}
                                        actionObject={actionObject}
                                        actionId={deliver._id}
                                    />
                                ))
                            )}

                            {loader && (
                                <Box sx={{
                                    position: "absolute",
                                    inset: "0",
                                    width: "100%",
                                    height: "100%",
                                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                    display: "grid",
                                    placeContent: "center"
                                }}>
                                    <Spinner />
                                </Box>
                            )}
                        </Box>

                        <Flex className="mt-4 row" sx={{ paddingX: "2rem" }}>
                            <Box className="col-3">
                                <Inputform
                                    inputClassName="custom-datepicker-input"
                                    wrapperClassName="date-input-wrapper"
                                    label="Delivery Date"
                                    Placeholder="Date"
                                    Name="deliveryDate"
                                    sx={{ color: label }}
                                    type="date"
                                    Title_sx={{ color: "grey3" }}
                                    touched={touched?.deliveryDate}
                                    errors={errors?.deliveryDate}
                                    values={values.deliveryDate}
                                    onBlur={handleBlur}
                                    OnChange={(e: any) => setFieldValue("deliveryDate", e.target.value)}
                                />
                            </Box>
                            <Box className="col-3">
                                <Inputform
                                    label="Email Link"
                                    Placeholder=""
                                    Name="emailLink"
                                    type="text"
                                    sx={{ color: label }}
                                    Title_sx={{ color: "grey3" }}
                                    touched={touched?.emailLink}
                                    errors={errors?.emailLink}
                                    values={values.emailLink}
                                    onBlur={handleBlur}
                                    OnChange={(e: any) => setFieldValue("emailLink", e.target.value, true)}
                                />
                            </Box>
                        </Flex>

                        <Box className="mt-5 row" sx={{ paddingX: "2rem" }}>
                            <Box >
                                <Image src={warningIcon} alt="" sx={{ width: "20px", marginRight: '0.5rem' }} />
                                <Text sx={{ color: '#F39C11', textAlign: 'left' }}>Warning</Text></Box>
                            <Text as='ol' sx={{ paddingX: "2rem", marginTop: 2 }}>

                                <Text variant="Poppins14Normal" as='li' sx={{ color: '#F39C11', textAlign: 'left' }}>Check questions in threads</Text>
                                <Text variant="Poppins14Normal" as='li' sx={{ color: '#F39C11', textAlign: 'left' }}>check need info.</Text>
                                <Text variant="Poppins14Normal" as='li' sx={{ color: '#F39C11', textAlign: 'left' }}>check blocked by info etc. </Text>
                                <Text variant="Poppins14Normal" as='li' sx={{ color: '#F39C11', textAlign: 'left' }}>++ If the all the delivery notes items are not checked then show a mandatory warning.</Text>

                            </Text>
                        </Box>
                    </ModalBody>

                    <ModalFooter style={{ backgroundColor: modalHeader, flexShrink: 0 }}>
                        <Button variant="CancelButton" sx={{ mr: 2 }} onClick={togglePopover}>Cancel</Button>
                        <Button type="submit" variant="SaveButton">Deliver</Button>
                    </ModalFooter>
                </form>
            </Modal>

            <Modal isOpen={showConfirmation} toggle={() => setShowConfirmation(false)} size="sm" centered>
                <ModalHeader toggle={() => setShowConfirmation(false)} style={{ backgroundColor: modalHeader, color: titleHeader }}>Confirm Delivery</ModalHeader>
                <ModalBody style={{ backgroundColor: modalHeader }}>
                    <Text style={{ color: titleHeader }}>Are you sure you want to deliver?</Text>
                </ModalBody>
                <ModalFooter style={{ backgroundColor: modalHeader }}>
                    <Button variant="CancelButton" sx={{ mr: 2 }} onClick={() => setShowConfirmation(false)}>No</Button>
                    <Button variant="SaveButton" onClick={handleFinalConfirm}>Yes</Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default ActionDeliverNotes;
